import React, { useCallback, useContext, useEffect, useState } from "react";
import { Switch, Tooltip } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { useSnackbar } from "notistack";

import { ConfigurationContainer } from "../../../shared/ConfigurationContainer";
import { LabeledValue } from "../../../shared/LabeledValue";
import { FullSite } from "../../../../dtos/site";
import { AjaxResult } from "../../../../enums/ajaxResult";
import { useApi } from "../../../useApi";
import SignalRContext from "../../../../signalr/context";

import { SiteRoomAlarmIndicator } from "./SiteRoomAlarmIndicator";
import { SiteRoomStatusIndicator } from "./SiteRoomStatusIndicator";
import { SiteServiceStatusIndicator } from "./SiteServiceStatusIndicator";
import { EnableMaintenanceModeDialog } from "../../sites/EnableMaintenanceModeDialog";

interface Props {
    site: FullSite;
    dealerId: number | null;
}

export function SiteInfo({ site, dealerId }: Props) {
    const { maintenanceModeApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const { maintenanceModeStatus } = useContext(SignalRContext);
    const [showDialog, setShowDialog] = useState(false);
    const [isInMaintenanceMode, setIsInMaintenanceMode] = useState(false);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        maintenanceModeApi.getStatus(site.id).then((r) => {
            if (r.result === AjaxResult.Success && r.data) {
                setIsInMaintenanceMode(r.data.isInMaintenanceMode);
            }
        });
    }, [site.id, maintenanceModeApi]);

    useEffect(() => {
        const signalRStatus = maintenanceModeStatus.get(site.id);
        if (signalRStatus !== undefined) {
            setIsInMaintenanceMode(signalRStatus.isEnabled);
        }
    }, [maintenanceModeStatus, site.id]);

    const handleDisable = useCallback(() => {
        if (!window.confirm("Are you sure you want to disable maintenance mode?")) {
            return;
        }
        setProcessing(true);
        maintenanceModeApi.disable(site.id)
            .then((r) => {
                if (r.result === AjaxResult.Success) {
                    setIsInMaintenanceMode(false);
                    enqueueSnackbar("Maintenance mode disabled", { variant: "success" });
                } else {
                    const msg = r.messages?.join(", ") ?? "Failed to disable maintenance mode";
                    enqueueSnackbar(msg, { variant: "error" });
                }
            })
            .catch(() => enqueueSnackbar("Failed to disable maintenance mode", { variant: "error" }))
            .finally(() => setProcessing(false));
    }, [maintenanceModeApi, site.id, enqueueSnackbar]);

    const handleToggle = useCallback((_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        if (checked) {
            setShowDialog(true);
        } else {
            handleDisable();
        }
    }, [handleDisable]);

    return (
        <>
            <ConfigurationContainer>
                <LabeledValue
                    label="Site Name"
                    value={site.name}
                />
                <LabeledValue
                    label="Unique Site Id"
                    value={site.uniqueSiteId}
                />
                <LabeledValue
                    label="Service Version"
                    value={site.acsVersion}
                />
                <LabeledValue
                    label="Service State"
                    value={<SiteServiceStatusIndicator siteId={site.id} />}
                />
                <LabeledValue
                    label="Room Alarms"
                    value={<SiteRoomAlarmIndicator site={site} dealerId={dealerId} />}
                />
                <LabeledValue
                    label="Room State"
                    value={<SiteRoomStatusIndicator siteId={site.id} />}
                />
                <LabeledValue
                    label={
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            Maintenance Mode
                            <Tooltip title="Enable to temporarily suppress email notifications while performing service work or testing to avoid triggering alerts for customers">
                                <InfoOutlined style={{ fontSize: "1rem", opacity: 0.5, cursor: "help" }} />
                            </Tooltip>
                        </span>
                    }
                    value={
                        <Switch
                            color="primary"
                            size="small"
                            checked={isInMaintenanceMode}
                            disabled={processing}
                            onChange={handleToggle}
                        />
                    }
                />
            </ConfigurationContainer>

            <EnableMaintenanceModeDialog
                open={showDialog}
                onClose={() => setShowDialog(false)}
                siteId={site.id}
                onSuccess={() => setIsInMaintenanceMode(true)}
            />
        </>
    );
}
